import { useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  useRouteError,
} from "react-router-dom";
import { UnsavedChangesProvider } from "@/components/shared/UnsavedChangesProvider";
import { useAuthStore } from "@/stores/auth";
import { usePermissionsStore } from "@/stores/permissions";
import { useBusinessUnitStore } from "@/stores/businessUnit";
import { useChatStore } from "@/features/ai/stores/chat";
import { clearSessionCache } from "./queryClient";
import { Providers } from "./Providers";
import { Router } from "./Router";
import { fetchCompany } from "@/features/settings/api/settingsService";
import { ensureZohoCustomer } from "@/features/settings/api/subscriptionService";
import { authIdentity } from "@/utils/token";

function RethrowRouteError(): never {
  // Keep the app's existing error screen and retry behavior for route errors.
  throw useRouteError();
}

const browserRouter = createBrowserRouter([
  {
    path: "*",
    errorElement: <RethrowRouteError />,
    element: (
      <UnsavedChangesProvider
        canBlock={() => Boolean(useAuthStore.getState().user)}
      >
        <Router />
      </UnsavedChangesProvider>
    ),
  },
]);

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);
  const token = useAuthStore((s) => s.token);
  const loading = useAuthStore((s) => s.loading);
  const user = useAuthStore((s) => s.user);
  const identity = authIdentity(token);

  useEffect(() => {
    initialize();
    const handleSessionExpired = () => {
      useAuthStore.getState().forceLogout();
      window.location.href = "/login";
    };
    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () =>
      window.removeEventListener("auth:session-expired", handleSessionExpired);
  }, [initialize]);

  useEffect(() => {
    if (identity) {
      usePermissionsStore.getState().reset();
      void usePermissionsStore.getState().load();
    } else if (!loading) {
      /* Only clear session-scoped state once auth has resolved. On a reload the
         token is still null while `initialize` rehydrates it, and resetting
         here wiped the persisted business-unit selection on every refresh. */
      usePermissionsStore.getState().reset();
      useChatStore.getState().resetChat();
      useBusinessUnitStore.getState().reset();
      clearSessionCache();
    }
  }, [identity, loading]);

  useEffect(() => {
    if (!user || user.role !== 1 || !token) return;
    // New registrations cannot authenticate until approved. Provision the
    // billing customer once they first sign in, using a protected endpoint.
    // The subscription settings page can retry if billing was unavailable.
    void fetchCompany()
      .then((company) => {
        if (!company.name || !company.id) return;
        return ensureZohoCustomer({
          email: company.email || user.email,
          companyName: company.name,
          companyId: company.id,
          user: user.userId,
        });
      })
      .catch(() => {
        // Billing availability must not prevent a successful authentication.
      });
    // A refresh changes the token but not the signed-in identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <Providers>
      <RouterProvider router={browserRouter} />
    </Providers>
  );
}
